import { message, Modal, Select } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import gql from 'graphql-tag';

const { Option } = Select;

const MUTATION_UPDATE_LANDLORD = gql`
  mutation updateLandlord($input: UpdateLandlordInput!) {
    updateLandlord(input: $input) {
      landlord {
        id
      }
    }
  }
`;

const useStyles = makeStyles(
  theme => ({
    modalTitle: {
      fontSize: 18,
      lineHeight: '24px',
      color: '#9e9e9e',
      marginBottom: 16,
    },
    modalSummary: {
      fontSize: 14,
      lineHeight: '22px',
      marginBottom: 20,
      color: theme.colors.bodyColor,
    },
    selectLabel: {
      fontSize: 14,
      lineHeight: '22px',
      marginBottom: 8,
      color: theme.colors.titleColor,

      '& > span': {
        color: '#FF4D4F',
        marginRight: 4,
      },
    },
  }),
  { name: 'changeContractStage' },
);

const ChangeContractStageModal = ({ active, t, modalValue, onClose, onConfirm, onChange }) => {
  const classes = useStyles();
  return (
    <Modal
      visible={active}
      okText={t('cms.landlord.modal.confirm.button')}
      onCancel={onClose}
      onOk={onConfirm}
      width={446}
      okButtonProps={{ size: 'large' }}
      cancelButtonProps={{ size: 'large' }}
      wrapClassName={'change-contract-stage'}
    >
      <p className={classes.modalTitle}>{t('cms.landlord.change_contract_stage.modal.title')}</p>
      <p className={classes.modalSummary}>
        {t('cms.landlord.change_contract_stage.modal.summary')}
      </p>
      <p className={classes.selectLabel}>
        <span>*</span>
        {t('cms.landlord.change_contract_stage.modal.label')}
      </p>
      <Select defaultValue={modalValue} style={{ width: 382, marginBottom: 8 }} onChange={onChange}>
        <Option value="NEW">{t('cms.landlord.contract_stage.new')}</Option>
        <Option value="DUALLY_SIGNED">{t('cms.landlord.contract_stage.dually_signed')}</Option>
      </Select>
    </Modal>
  );
};

const ChangeContractStageBtn = ({ onClick }) => {
  const { t } = useTranslation();
  const ref = useRef();

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={onClick}>
      {t('cms.property_card.action.btn.edit')}
    </div>
  );
};

const useChangeContractStage = ({ landlord, successCallBack }) => {
  const { t } = useTranslation();

  const [modalValue, setModalValue] = useState(landlord?.contractStage || 'NEW');
  const [showModal, setShowModal] = useState(false);

  const [updateLandlord, { loading }] = useMutation(MUTATION_UPDATE_LANDLORD, {
    variables: {
      input: {
        id: landlord.id,
        contractStage: modalValue,
      },
    },
    onCompleted: () => {
      successCallBack();
      message.success(t('this_landlord_was_successfully_updated'));
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('this_landlord_was_failed_to_update_please_try_again'));
      }
    },
  });

  const handleChange = value => {
    setModalValue(value);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleConfirm = () => {
    if (modalValue !== landlord?.contractStage) {
      updateLandlord();
    } else {
      setShowModal(false);
      message.success(t('this_landlord_was_successfully_updated'));
    }
  };

  return {
    ChangeContractStageBtnComponent: (
      <ChangeContractStageBtn
        loading={loading}
        onClick={() => {
          setShowModal(true);
        }}
      />
    ),
    ChangeContractStageModalComponent: (
      <ChangeContractStageModal
        active={showModal}
        modalValue={modalValue}
        onChange={handleChange}
        t={t}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    ),
  };
};

export default useChangeContractStage;
